import { motion, AnimatePresence } from "framer-motion";
import * as React from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

interface MotionModalProps {
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
}

export default function MotionModal({
  isOpen,
  children,
  className = "",
}: MotionModalProps) {
  useEffect(() => {
    const clearBlurAndRestore = () => {
      const scrollY = document.body.getAttribute("data-scroll-position");

      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.body.removeAttribute("data-scroll-position");

      const rootElement = document.getElementById("root");
      if (rootElement) {
        const childElements = Array.from(rootElement.children);
        childElements.forEach(child => {
          if (child instanceof HTMLElement) {
            child.style.filter = "";
            child.style.transition = "";
            child.style.pointerEvents = "";
            child.style.userSelect = "";
          }
        });
      }

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
      }
    };

    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.setAttribute("data-scroll-position", scrollY.toString());

      document.body.style.overflow = "hidden";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      const rootElement = document.getElementById("root");
      if (rootElement) {
        const childElements = Array.from(rootElement.children);
        childElements.forEach(child => {
          if (child instanceof HTMLElement) {
            const computedStyle = window.getComputedStyle(child);
            if (computedStyle.position !== "fixed") {
              child.style.filter = "blur(4px)";
              child.style.transition = "filter 0.3s ease-in-out";
              child.style.pointerEvents = "none";
              child.style.userSelect = "none";
            }
          }
        });
      }
    } else {
      clearBlurAndRestore();
    }

    return () => clearBlurAndRestore();
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 1, y: "100%" }}
            animate={{ opacity: 1, y: "0%" }}
            exit={{ opacity: 1, y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={`fixed z-[999] bottom-0 w-full max-w-[430px] ${className} openedModalOnScreen`}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
